import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { CashVoucherFormModal } from '../components/CashVoucherFormModal';
import { PaymentDetailModal } from '../components/PaymentDetailModal';
import { PaymentFormModal } from '../components/PaymentFormModal';
import {
  PaymentFilterModal,
  EMPTY_PAYMENT_FILTERS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_TYPE_LABELS,
  countActivePaymentFilters,
  type PaymentFilters,
} from '../components/PaymentFilterModal';
import { StatusBadge, TypePill } from '../components/StatusBadge';
import { TableActionMenu, type TableActionMenuItem } from '../components/TableActionMenu';
import { useAuth } from '../context/AuthContext';
import { approvalsApi } from '../lib/api/approvals';
import type { CreatePaymentInput, ListQuery, PaymentRequest, PaymentStatus } from '../lib/api/payments';
import { paymentsApi } from '../lib/api/payments';
import { exportPaymentsToExcel } from '../lib/export-excel';
import { formatDate, formatVND } from '../lib/format';

type Role = 'KTT' | 'NV_KT' | 'CEO' | 'NV';

/** Row bulk action user có thể làm được. */
type BulkAction = 'approve' | 'disburse' | null;

function bulkActionFor(role: Role | undefined, status: PaymentStatus): BulkAction {
  if (!role) return null;
  if (role === 'KTT' && status === 'PENDING_KTT') return 'approve';
  if (role === 'CEO' && status === 'PENDING_CEO') return 'approve';
  if ((role === 'NV_KT' || role === 'KTT') && status === 'APPROVED') return 'disburse';
  return null;
}

interface ColumnPref {
  date: boolean;
  requester: boolean;
  department: boolean;
  type: boolean;
  amount: boolean;
  reason: boolean;
  attach: boolean;
  status: boolean;
}
const DEFAULT_COLS: ColumnPref = {
  date: true, requester: true, department: true, type: true,
  amount: true, reason: true, attach: true, status: true,
};
const COL_LABELS: Record<keyof ColumnPref, string> = {
  date: 'Ngày tạo',
  requester: 'Người đề nghị',
  department: 'Phòng ban',
  type: 'Loại',
  amount: 'Số tiền',
  reason: 'Lý do',
  attach: 'Đính kèm',
  status: 'Trạng thái',
};
const COL_STORAGE_KEY = 'erp_ac_payments_cols';

const PAGE_SIZE = 20;

export function PaymentsPage() {
  const [items, setItems] = useState<PaymentRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentRequest | null>(null);
  const [duplicateFrom, setDuplicateFrom] = useState<PaymentRequest | null>(null);
  const [voucherFor, setVoucherFor] = useState<PaymentRequest | null>(null);
  const [detail, setDetail] = useState<PaymentRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<PaymentFilters>(EMPTY_PAYMENT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = countActivePaymentFilters(filters);

  const { user } = useAuth();
  const userRole = user?.role as Role | undefined;

  // Column visibility (persist)
  const [cols, setCols] = useState<ColumnPref>(() => {
    try {
      const raw = localStorage.getItem(COL_STORAGE_KEY);
      if (raw) return { ...DEFAULT_COLS, ...(JSON.parse(raw) as ColumnPref) };
    } catch { /* ignore */ }
    return DEFAULT_COLS;
  });
  const [colsOpen, setColsOpen] = useState(false);
  const colsWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(cols)); } catch { /* ignore */ }
  }, [cols]);
  useEffect(() => {
    if (!colsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (colsWrapRef.current && !colsWrapRef.current.contains(e.target as Node)) setColsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colsOpen]);

  // Row action menu — outside-click do backdrop trong popover xử lý (không cần document listener)
  const [rowMenuOpen, setRowMenuOpen] = useState<string | null>(null);
  // Khi menu open → ẩn 3-dots của TẤT CẢ row khác để tránh visual clutter
  useEffect(() => {
    if (rowMenuOpen) document.body.classList.add('pay-menu-open');
    else document.body.classList.remove('pay-menu-open');
    return () => document.body.classList.remove('pay-menu-open');
  }, [rowMenuOpen]);
  const [busyBulk, setBusyBulk] = useState(false);

  // Debounce search 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset về trang 1 khi đổi search / filter
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: ListQuery = { limit: PAGE_SIZE, page };
      if (debouncedSearch) query.search = debouncedSearch;
      if (filters.types.length === 1) query.type = filters.types[0];
      if (filters.statuses.length === 1) query.status = filters.statuses[0];
      if (filters.urgency !== null) query.urgency = filters.urgency;
      const res = await paymentsApi.list(query);
      setItems(res.items);
      setTotal(res.paging.total);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (input: CreatePaymentInput, submitAfter?: boolean) => {
    const created = await paymentsApi.create(input);
    if (submitAfter) {
      await paymentsApi.submit(created.id);
    }
    await load();
  };
  const handleUpdate = async (input: CreatePaymentInput, submitAfter?: boolean) => {
    if (!editing) return;
    const updated = await paymentsApi.update(editing.id, input);
    // Chỉ submit-after nếu đang từ DRAFT → PENDING_KTT. Phiếu đã submit rồi thì skip.
    if (submitAfter && editing.status === 'DRAFT') {
      await paymentsApi.submit(updated.id);
    }
    setEditing(null);
    await load();
  };
  const openDetail = async (item: PaymentRequest) => {
    try {
      setDetail(await paymentsApi.get(item.id));
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const handleDelete = async (data: PaymentRequest) => {
    if (!confirm(`Xoá phiếu ${data.code}?`)) return;
    try {
      await paymentsApi.remove(data.id);
      setDetail(null);
      await load();
    } catch (err) {
      alert((err as Error).message);
    }
  };
  const handleSubmit = async (data: PaymentRequest) => {
    if (!confirm(`Gửi phiếu ${data.code} đi duyệt?`)) return;
    try {
      await paymentsApi.submit(data.id);
      setDetail(null);
      await load();
    } catch (err) {
      alert((err as Error).message);
    }
  };
  const handleDisburse = async (data: PaymentRequest, proofAttachmentIds: string[]) => {
    await paymentsApi.disburse(data.id, proofAttachmentIds);
    setDetail(null);
    await load();
  };
  const handleCancel = async (data: PaymentRequest) => {
    await paymentsApi.cancel(data.id);
    setDetail(null);
    await load();
  };

  const toggleSel = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const clearSel = () => setSelected([]);

  // Client-side filter (đảm bảo đúng khi filter multi-select vượt khả năng BE param)
  const filteredItems = useMemo(() => {
    return items.filter((i) => {
      if (filters.types.length > 0 && !filters.types.includes(i.type)) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(i.status)) return false;
      if (filters.urgency !== null) {
        const isUrgent = i.urgency || i.advanceType === 'urgent';
        if (filters.urgency !== isUrgent) return false;
      }
      return true;
    });
  }, [items, filters]);

  const selectedItems = useMemo(
    () => filteredItems.filter((i) => selected.includes(i.id)),
    [filteredItems, selected],
  );
  const selTotal = selectedItems.reduce((a, i) => a + i.amount, 0);

  // Suy ra bulk action từ selection (chỉ hợp lệ khi tất cả cùng status + user có quyền)
  const bulkKind: BulkAction = useMemo(() => {
    if (selectedItems.length === 0) return null;
    const first = bulkActionFor(userRole, selectedItems[0].status);
    if (!first) return null;
    for (const i of selectedItems) {
      if (bulkActionFor(userRole, i.status) !== first) return null;
    }
    return first;
  }, [selectedItems, userRole]);

  // Rows user có thể tick (đủ quyền bulk)
  const selectableIds = useMemo(
    () => filteredItems.filter((i) => bulkActionFor(userRole, i.status) !== null).map((i) => i.id),
    [filteredItems, userRole],
  );

  const resolveApprovalId = async (prId: string): Promise<string> => {
    const list = await approvalsApi.list({ status: 'PENDING', limit: 100 });
    const target = list.items.find((a) => a.paymentRequestId === prId);
    if (!target) throw new Error(`Không tìm thấy approval PENDING cho phiếu ${prId}`);
    return target.id;
  };

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) return;
    if (!confirm(`Duyệt ${selectedItems.length} phiếu · Tổng ${formatVND(selTotal)}?`)) return;
    setBusyBulk(true);
    try {
      const ids = await Promise.all(selectedItems.map((i) => resolveApprovalId(i.id)));
      const res = await approvalsApi.bulkApprove(ids);
      if (res.failed.length > 0) alert(`Duyệt ${res.approved} · thất bại ${res.failed.length}`);
      clearSel();
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusyBulk(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedItems.length === 0) return;
    const reason = prompt(`Từ chối ${selectedItems.length} phiếu — lý do (≥ 10 ký tự):`);
    if (!reason || reason.length < 10) return;
    setBusyBulk(true);
    try {
      for (const i of selectedItems) {
        try {
          const id = await resolveApprovalId(i.id);
          await approvalsApi.reject(id, reason);
        } catch { /* skip */ }
      }
      clearSel();
      await load();
    } finally {
      setBusyBulk(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    // Fetch tất cả bằng loop trang (BE cap limit=100/page). Apply filter client-side.
    setExporting(true);
    try {
      const baseQuery: ListQuery = { limit: 100 };
      if (debouncedSearch) baseQuery.search = debouncedSearch;
      if (filters.types.length === 1) baseQuery.type = filters.types[0];
      if (filters.statuses.length === 1) baseQuery.status = filters.statuses[0];
      if (filters.urgency !== null) baseQuery.urgency = filters.urgency;

      const acc: PaymentRequest[] = [];
      let pageNum = 1;
      const MAX_PAGES = 50; // safety: 5000 rows tối đa
      while (pageNum <= MAX_PAGES) {
        const res = await paymentsApi.list({ ...baseQuery, page: pageNum });
        acc.push(...res.items);
        if (!res.paging.hasNextPage) break;
        pageNum++;
      }

      const all = acc.filter((i) => {
        if (filters.types.length > 0 && !filters.types.includes(i.type)) return false;
        if (filters.statuses.length > 0 && !filters.statuses.includes(i.status)) return false;
        if (filters.urgency !== null) {
          const isUrgent = i.urgency || i.advanceType === 'urgent';
          if (filters.urgency !== isUrgent) return false;
        }
        return true;
      });
      if (all.length === 0) {
        alert('Không có phiếu nào để xuất.');
        return;
      }
      exportPaymentsToExcel(all);
    } catch (e) {
      alert('Xuất Excel lỗi: ' + (e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const handleBulkDisburse = async () => {
    if (selectedItems.length === 0) return;
    alert('Mỗi phiếu cần có ảnh xác nhận đã chi. Vui lòng mở chi tiết từng phiếu để tải ảnh và xác nhận chi tiền.');
  };

  return (
    <AppShell
      title="Trung tâm thanh toán"
      subtitle="Lập đề nghị chi · Theo dõi trạng thái duyệt · Xem lịch sử phiếu chi"
    >
      <div className="pay-page-card">
        {/* Top row: search + filter | Xuất Excel + Cột hiển thị + Lập ĐNC (dark) */}
        <div className="pay-toolbar">
          <div className="pay-toolbar-left">
            <div className="pay-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input
                placeholder="Tìm mã đề nghị, người đề nghị..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className="pay-btn-outline" onClick={() => setFilterOpen(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="21" x2="14" y1="4" y2="4" />
                <line x1="10" x2="3" y1="4" y2="4" />
                <line x1="21" x2="12" y1="12" y2="12" />
                <line x1="8" x2="3" y1="12" y2="12" />
                <line x1="21" x2="16" y1="20" y2="20" />
                <line x1="12" x2="3" y1="20" y2="20" />
                <line x1="14" x2="14" y1="2" y2="6" />
                <line x1="8" x2="8" y1="10" y2="14" />
                <line x1="16" x2="16" y1="18" y2="22" />
              </svg>
              Bộ lọc
              {activeFilterCount > 0 && <span className="apr-filter-badge">{activeFilterCount}</span>}
            </button>
          </div>
          <div className="pay-toolbar-right">
            {userRole !== 'NV' && (
              <button className="pay-btn-outline" onClick={handleExport} disabled={exporting}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
                {exporting ? 'Đang xuất…' : 'Xuất Excel'}
              </button>
            )}
            <div className="pay-cols-wrap" ref={colsWrapRef}>
              <button className="pay-btn-outline" onClick={() => setColsOpen((v) => !v)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18M15 3v18" />
                </svg>
                Cột hiển thị
              </button>
              {colsOpen && (
                <div className="pay-cols-menu">
                  <div className="pay-cols-head">Cột hiển thị</div>
                  {(Object.keys(COL_LABELS) as (keyof ColumnPref)[]).map((k) => (
                    <label key={k} className="pay-cols-item">
                      <input
                        type="checkbox"
                        checked={cols[k]}
                        onChange={() => setCols((c) => ({ ...c, [k]: !c[k] }))}
                      />
                      <span>{COL_LABELS[k]}</span>
                    </label>
                  ))}
                  <div className="pay-cols-foot">
                    <button className="pay-cols-reset" onClick={() => setCols(DEFAULT_COLS)}>Đặt lại</button>
                  </div>
                </div>
              )}
            </div>
            <button className="pay-btn-dark" onClick={() => { setEditing(null); setFormOpen(true); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5v14" />
              </svg>
              Lập đề nghị chi
            </button>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="apr-filter-chips" style={{ padding: '0 20px 12px' }}>
            {filters.types.map((t) => (
              <span key={t} className="apr-filter-chip">
                Loại: {PAYMENT_TYPE_LABELS[t]}
                <button onClick={() => setFilters((f) => ({ ...f, types: f.types.filter((x) => x !== t) }))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
            {filters.statuses.map((s) => (
              <span key={s} className="apr-filter-chip">
                Trạng thái: {PAYMENT_STATUS_LABELS[s]}
                <button onClick={() => setFilters((f) => ({ ...f, statuses: f.statuses.filter((x) => x !== s) }))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
            {filters.urgency !== null && (
              <span className="apr-filter-chip">
                {filters.urgency ? 'Khẩn / Đột xuất' : 'Bình thường'}
                <button onClick={() => setFilters((f) => ({ ...f, urgency: null }))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            <button className="apr-filter-clear" onClick={() => setFilters(EMPTY_PAYMENT_FILTERS)}>
              Xoá tất cả
            </button>
          </div>
        )}

        {/* Bulk action bar */}
        {selected.length > 0 && (
          <div className="pay-bulk">
            <div className="pay-bulk-info">
              Đã chọn <b>{selected.length}</b> mục · Tổng <b>{formatVND(selTotal)}</b>
            </div>
            <div className="pay-bulk-actions">
              {bulkKind === 'approve' && (
                <>
                  <button className="pay-btn-outline danger" onClick={handleBulkReject} disabled={busyBulk}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></svg>
                    Từ chối
                  </button>
                  <button className="pay-btn-primary" onClick={handleBulkApprove} disabled={busyBulk}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                    {busyBulk ? 'Đang xử lý…' : 'Duyệt hàng loạt'}
                  </button>
                </>
              )}
              {bulkKind === 'disburse' && (
                <button className="pay-btn-primary" onClick={handleBulkDisburse} disabled={busyBulk}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>
                  {busyBulk ? 'Đang xử lý…' : 'Xác nhận đã chi hàng loạt'}
                </button>
              )}
              {bulkKind === null && (
                <span className="pay-bulk-hint">Các phiếu chọn không cùng hành động phù hợp với vai trò của bạn</span>
              )}
              <button className="pay-bulk-clear" onClick={clearSel}>Bỏ chọn</button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="pay-table-wrap">
          {error && <div className="pay-table-error">⚠️ {error}</div>}
          {loading ? (
            <div className="pay-empty">Đang tải...</div>
          ) : filteredItems.length === 0 ? (
            <div className="pay-empty">
              <div className="pay-empty-icon">📄</div>
              <div>Chưa có phiếu chi nào</div>
            </div>
          ) : (
            <table className="pay-tbl">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      checked={selectableIds.length > 0 && selected.length === selectableIds.length}
                      disabled={selectableIds.length === 0}
                      onChange={() =>
                        setSelected(selected.length === selectableIds.length ? [] : selectableIds)
                      }
                    />
                  </th>
                  <th className="col-code">Mã đề nghị</th>
                  {cols.date && <th className="col-date">Ngày tạo</th>}
                  {cols.requester && <th className="col-req">Người đề nghị</th>}
                  {cols.department && <th className="col-dept">Phòng ban</th>}
                  {cols.type && <th className="col-type">Loại</th>}
                  {cols.amount && <th className="col-amount">Số tiền</th>}
                  {cols.reason && <th className="col-reason">Lý do</th>}
                  {cols.attach && <th className="col-attach">Đính kèm</th>}
                  {cols.status && <th className="col-status">Trạng thái</th>}
                  <th className="col-action">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((row) => {
                  const isSel = selected.includes(row.id);
                  const canBulk = bulkActionFor(userRole, row.status) !== null;
                  const initials = (row.requesterName || '?').split(' ').slice(-2).map((w) => w[0]).join('').toUpperCase();
                  return (
                    <tr
                      key={row.id}
                      className={`pay-row ${isSel ? 'selected' : ''}`}
                      data-menu-active={rowMenuOpen === row.id ? '' : undefined}
                      onClick={() => openDetail(row)}
                    >
                      <td className="col-check" onClick={(e) => e.stopPropagation()}>
                        {canBulk ? (
                          <input type="checkbox" checked={isSel} onChange={() => toggleSel(row.id)} />
                        ) : (
                          <span title="Phiếu này ngoài phạm vi thao tác hàng loạt của bạn">—</span>
                        )}
                      </td>
                      <td className="col-code" style={{ fontFamily: 'inherit', color: '#172033', fontWeight: 500 }}>{row.code}</td>
                      {cols.date && <td className="col-date" style={{ color: '#64748b' }}>{formatDate(row.createdAt)}</td>}
                      {cols.requester && (
                        <td className="col-req">
                          <div className="pay-req-cell">
                            <div className="pay-req-avatar">{initials}</div>
                            <div>
                              <div className="pay-req-name">{row.requesterName || row.requesterId}</div>
                              <div className="pay-req-role">{row.department || '—'}</div>
                            </div>
                          </div>
                        </td>
                      )}
                      {cols.department && <td className="col-dept" style={{ color: '#64748b' }}>{row.department || '—'}</td>}
                      {cols.type && (
                        <td className="col-type">
                          <TypePill type={row.type} advanceType={row.advanceType} />
                        </td>
                      )}
                      {cols.amount && (
                        <td className="col-amount" style={{ fontWeight: 600, textAlign: 'right', color: '#172033', fontFamily: 'inherit' }}>
                          {formatVND(row.amount)}
                        </td>
                      )}
                      {cols.reason && <td className="col-reason" title={row.reason}>{row.reason}</td>}
                      {cols.attach && (
                        <td className="col-attach" style={{ textAlign: 'center', color: '#64748b' }}>
                          {row.attachments && row.attachments.length > 0 ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                              {row.attachments.length}
                            </span>
                          ) : '—'}
                        </td>
                      )}
                      {cols.status && (
                        <td className="col-status">
                          <StatusBadge status={row.status} urgent={row.urgency || row.advanceType === 'urgent'} />
                        </td>
                      )}
                      <td className="col-action" onClick={(e) => e.stopPropagation()}>
                        <RowActionMenu
                          row={row}
                          user={user}
                          open={rowMenuOpen === row.id}
                          onToggle={() => setRowMenuOpen(rowMenuOpen === row.id ? null : row.id)}
                          onClose={() => setRowMenuOpen(null)}
                          onView={() => openDetail(row)}
                          onEdit={() => { setEditing(row); setFormOpen(true); }}
                          onDelete={() => handleDelete(row)}
                          onSubmit={() => handleSubmit(row)}
                          onDisburse={() => openDetail(row)}
                          onCancel={async () => {
                            if (!confirm(`Huỷ phiếu ${row.code}?`)) return;
                            try {
                              await handleCancel(row);
                            } catch (e) {
                              alert((e as Error).message);
                            }
                          }}
                          onApprove={async () => {
                            if (!confirm(`Duyệt phiếu ${row.code}?`)) return;
                            try {
                              const id = await resolveApprovalId(row.id);
                              await approvalsApi.approve(id);
                              await load();
                            } catch (e) {
                              alert((e as Error).message);
                            }
                          }}
                          onReject={async () => {
                            const reason = prompt('Lý do từ chối (≥ 10 ký tự):');
                            if (!reason || reason.length < 10) {
                              if (reason !== null) alert('Lý do tối thiểu 10 ký tự');
                              return;
                            }
                            try {
                              const id = await resolveApprovalId(row.id);
                              await approvalsApi.reject(id, reason);
                              await load();
                            } catch (e) {
                              alert((e as Error).message);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="apr-pagination" style={{ padding: '16px 20px' }}>
            <div className="apr-pag-info">
              Hiển thị {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} của {total} phiếu
            </div>
            <div className="apr-pag-nav">
              <button
                className="apr-pag-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ◀ Trước
              </button>
              <button className="apr-pag-btn apr-pag-current">{page}</button>
              <button
                className="apr-pag-btn"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Sau ▶
              </button>
            </div>
          </div>
        )}
      </div>

      <PaymentFormModal
        open={formOpen}
        editing={editing}
        duplicateFrom={duplicateFrom}
        onClose={() => { setFormOpen(false); setEditing(null); setDuplicateFrom(null); }}
        onSubmit={editing ? handleUpdate : handleCreate}
      />

      <PaymentDetailModal
        open={!!detail}
        data={detail}
        onClose={() => setDetail(null)}
        onEdit={(d) => { setDetail(null); setEditing(d); setFormOpen(true); }}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        onDisburse={handleDisburse}
        onCreateVoucher={(d) => { setDetail(null); setVoucherFor(d); }}
        onCancel={handleCancel}
        onDuplicate={(d) => {
          setDetail(null);
          setEditing(null);
          setDuplicateFrom(d);
          setFormOpen(true);
        }}
        onActionDone={load}
      />

      <CashVoucherFormModal
        open={!!voucherFor}
        paymentRequest={voucherFor}
        onClose={() => setVoucherFor(null)}
        onSuccess={load}
      />

      <PaymentFilterModal
        open={filterOpen}
        initial={filters}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
      />
    </AppShell>
  );
}

// ============================================================
// Row action menu (3 dots) — actions theo role + status
// ============================================================

interface RowActionMenuProps {
  row: PaymentRequest;
  user: ReturnType<typeof useAuth>['user'];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSubmit: () => void;
  onDisburse: () => void;
  onCancel: () => void;
  onApprove: () => void;
  onReject: () => void;
}

function RowActionMenu({
  row, user, open, onToggle, onClose,
  onView, onEdit, onDelete, onSubmit, onDisburse, onCancel, onApprove, onReject,
}: RowActionMenuProps) {
  const isOwner = row.requesterId === user?.id;
  const isDraft = row.status === 'DRAFT';
  const isPendingNvkt = row.status === 'PENDING_NVKT';
  const isPendingKtt = row.status === 'PENDING_KTT';
  const canEdit = isOwner && (isDraft || isPendingNvkt || isPendingKtt);
  const canDelete = isOwner && (isDraft || row.status === 'CANCELLED');
  const canCancel = isOwner && (isPendingNvkt || isPendingKtt);
  const role = user?.role;
  const canApprove =
    (role === 'KTT' && row.status === 'PENDING_KTT') ||
    (role === 'CEO' && row.status === 'PENDING_CEO');
  // "Xác nhận đã chi" — chỉ khi APPROVED + đã có PC (từ flow mới).
  const canDisburse =
    row.status === 'APPROVED' && !!row.cashVoucher && (role === 'KTT' || role === 'NV_KT');

  const items: TableActionMenuItem[] = [
    { label: 'Xem chi tiết', onClick: onView },
    ...(canEdit ? [{ label: 'Sửa', onClick: onEdit }] : []),
    ...(isDraft && isOwner ? [{ label: 'Gửi duyệt', onClick: onSubmit }] : []),
    ...(canApprove ? [{ label: 'Duyệt', tone: 'success' as const, onClick: onApprove }, { label: 'Từ chối', tone: 'danger' as const, onClick: onReject }] : []),
    ...(canDisburse ? [{ label: 'Xác nhận đã chi', tone: 'success' as const, onClick: onDisburse }] : []),
    ...(canDelete ? [{ label: 'Xoá phiếu', tone: 'danger' as const, dividerBefore: true, onClick: onDelete }] : []),
    ...(canCancel ? [{ label: 'Huỷ phiếu', tone: 'danger' as const, dividerBefore: true, onClick: onCancel }] : []),
  ];

  return <TableActionMenu open={open} onToggle={onToggle} onClose={onClose} items={items} />;
}
